import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, apiError } from '../../lib/api';

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export default function OwnerBusinessDetail() {
  const { id } = useParams();
  const [biz, setBiz] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('info');

  const load = async () => {
    try {
      const { data } = await api.get('/owner/businesses');
      const b = (data.businesses||[]).find(x => String(x.id) === String(id));
      if (!b) { setErr('Not found'); return; }
      const { data: full } = await api.get(`/businesses/${b.slug}`);
      setBiz(full.business);
    } catch (e) { setErr(apiError(e)); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const saveInfo = async (payload) => {
    await api.put(`/owner/businesses/${id}`, payload);
    await load();
    alert('Business updated');
  };

  if (err) return <div className="alert alert-danger">{err}</div>;
  if (!biz) return <div><span className="ap-loader"/></div>;

  return (
    <div data-testid="owner-biz-detail">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <div className="small text-muted-2"><Link to="/owner/businesses">← Back</Link></div>
          <h2 className="mb-0">{biz.name}</h2>
          <div className="small text-muted-2">{biz.category_name} · {biz.city_name} · <span className={'pill pill-'+biz.status}>{biz.status}</span></div>
        </div>
        <a href={`/business/${biz.slug}`} className="btn-ap-outline" data-testid="view-public">View public page →</a>
      </div>

      <div className="d-flex gap-2 mb-3 flex-wrap">
        {[['info','Info'],['gallery','Gallery'],['products','Products'],['services','Services'],['hours','Business hours']].map(([k,l]) => (
          <button key={k} className={tab===k?'btn-ap':'btn-ap-outline'} onClick={()=>setTab(k)} data-testid={`tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === 'info' && (
        <div className="ap-card">
          <BusinessInfoForm initial={biz} onSubmit={saveInfo} submitLabel="Save changes" />
        </div>
      )}

      {tab === 'gallery' && <Gallery biz={biz} reload={load} />}
      {tab === 'products' && <Products biz={biz} reload={load} />}
      {tab === 'services' && <Services biz={biz} reload={load} />}
      {tab === 'hours' && <Hours biz={biz} reload={load} />}
    </div>
  );
}

/* ============================================================
   Reusable image field with URL + Upload (used for Logo, Cover, Gallery)
   ============================================================ */
function ImageField({
  label,
  value,
  onChange,
  aspect = 'square',  // 'square' | 'wide'
  previewSize = { width: 64, height: 64 },
  testIdPrefix = 'img',
  helpText = 'JPG, PNG, GIF or WEBP · max 5 MB',
}) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  const uploadFile = async (file) => {
    if (!file) return;
    setErr('');
    if (file.size > 5 * 1024 * 1024) {
      setErr('Image is too large. Max size is 5 MB.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url =
        data?.url || data?.file_url || data?.image_url ||
        data?.path || data?.data?.url || '';
      if (!url) throw new Error('Upload succeeded but no URL was returned.');
      onChange(url);
    } catch (e) {
      setErr(
        'Upload failed: ' +
        (apiError(e) || e.message || 'Unknown error') +
        '. You can still paste an image URL.'
      );
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = '';
  };

  return (
    <div>
      <label className="form-label small fw-semibold mb-1">{label}</label>
      {err && <div className="alert alert-danger py-1 px-2 small mb-2">{err}</div>}

      <div className="d-flex gap-2 align-items-start">
        {/* Preview box */}
        <div
          style={{
            width: previewSize.width,
            height: previewSize.height,
            flex: `0 0 ${previewSize.width}px`,
            borderRadius: aspect === 'wide' ? 10 : 12,
            border: '1px dashed #cbd5e1',
            background: '#F8FAFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {value ? (
            <img
              src={value}
              alt={`${label} preview`}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                e.currentTarget.style.opacity = '0.3';
                e.currentTarget.title = 'Preview unavailable';
              }}
            />
          ) : (
            <span className="text-muted small" style={{ fontSize: '0.7rem' }}>
              {aspect === 'wide' ? 'Cover' : 'Logo'}
            </span>
          )}
          {uploading && (
            <div
              style={{
                position: 'absolute', inset: 0,
                background: 'rgba(255,255,255,0.75)',
                display: 'flex', alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.65rem', fontWeight: 600, color: '#4338CA',
              }}
            >
              …
            </div>
          )}
        </div>

        {/* Input + buttons */}
        <div className="flex-grow-1">
          <div style={{ position: 'relative' }}>
            <input
              className="form-control"
              placeholder="Paste URL, or click Upload →"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              style={{ paddingRight: value ? 38 : 12 }}
              data-testid={`${testIdPrefix}-url`}
            />
            {value && (
              <button
                type="button"
                onClick={() => onChange('')}
                style={{
                  position: 'absolute', right: 6, top: '50%',
                  transform: 'translateY(-50%)',
                  border: 'none', background: 'transparent',
                  cursor: 'pointer', color: '#94a3b8',
                  fontSize: '1.1rem', lineHeight: 1,
                }}
                aria-label="Clear"
              >
                ×
              </button>
            )}
          </div>
          <div className="d-flex gap-2 mt-1">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid={`${testIdPrefix}-upload-btn`}
            >
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
            {value && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => onChange('')}
                disabled={uploading}
              >
                Remove
              </button>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={onPickFile}
            data-testid={`${testIdPrefix}-file`}
          />
          {helpText && (
            <div className="form-text small text-muted-2 mt-1">{helpText}</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Business Info Form with Logo + Cover uploads
   ============================================================ */
function BusinessInfoForm({ initial = {}, onSubmit, submitLabel = 'Save changes' }) {
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const [f, setF] = useState({
    name: initial.name || '',
    tagline: initial.tagline || '',
    description: initial.description || '',
    phone: initial.phone || '',
    whatsapp: initial.whatsapp || '',
    email: initial.email || '',
    address: initial.address || '',
    category_id: initial.category_id || '',
    city_id: initial.city_id || '',
    website: initial.website || '',
    logo_url: initial.logo_url || '',
    cover_url: initial.cover_url || '',
  });

  useEffect(() => {
    (async () => {
      try {
        const [c, ci] = await Promise.all([api.get('/categories'), api.get('/cities')]);
        setCategories(c.data.categories || []);
        setCities(ci.data.cities || []);
      } catch (e) { console.error(apiError(e)); }
    })();
  }, []);

  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!f.name.trim()) { setErr('Business name is required.'); return; }
    setSaving(true);
    try {
      await onSubmit(f);
    } catch (e) {
      setErr(apiError(e) || 'Failed to save business.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="row g-3" data-testid="business-info-form">
      {err && (
        <div className="col-12">
          <div className="alert alert-danger mb-0">{err}</div>
        </div>
      )}

      {/* --- Basic info --- */}
      <div className="col-md-6">
        <label className="form-label small fw-semibold">Business name *</label>
        <input className="form-control" required value={f.name} onChange={set('name')} data-testid="bf-name" />
      </div>
      <div className="col-md-6">
        <label className="form-label small fw-semibold">Tagline</label>
        <input className="form-control" value={f.tagline} onChange={set('tagline')} placeholder="Short one-liner" data-testid="bf-tagline" />
      </div>

      <div className="col-12">
        <label className="form-label small fw-semibold">Description</label>
        <textarea className="form-control" rows={3} value={f.description} onChange={set('description')} data-testid="bf-description" />
      </div>

      <div className="col-md-6">
        <label className="form-label small fw-semibold">Category</label>
        <select className="form-select" value={f.category_id} onChange={set('category_id')} data-testid="bf-category">
          <option value="">Select category</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="col-md-6">
        <label className="form-label small fw-semibold">City</label>
        <select className="form-select" value={f.city_id} onChange={set('city_id')} data-testid="bf-city">
          <option value="">Select city</option>
          {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {/* --- Contact --- */}
      <div className="col-md-4">
        <label className="form-label small fw-semibold">Phone</label>
        <input className="form-control" value={f.phone} onChange={set('phone')} data-testid="bf-phone" />
      </div>
      <div className="col-md-4">
        <label className="form-label small fw-semibold">WhatsApp</label>
        <input className="form-control" value={f.whatsapp} onChange={set('whatsapp')} data-testid="bf-whatsapp" />
      </div>
      <div className="col-md-4">
        <label className="form-label small fw-semibold">Email</label>
        <input className="form-control" type="email" value={f.email} onChange={set('email')} data-testid="bf-email" />
      </div>

      <div className="col-md-8">
        <label className="form-label small fw-semibold">Address</label>
        <input className="form-control" value={f.address} onChange={set('address')} data-testid="bf-address" />
      </div>
      <div className="col-md-4">
        <label className="form-label small fw-semibold">Website</label>
        <input className="form-control" value={f.website} onChange={set('website')} placeholder="https://" data-testid="bf-website" />
      </div>

      {/* --- Logo --- */}
      <div className="col-md-6">
        <ImageField
          label="Logo"
          value={f.logo_url}
          onChange={(url) => setF((prev) => ({ ...prev, logo_url: url }))}
          aspect="square"
          previewSize={{ width: 64, height: 64 }}
          testIdPrefix="bf-logo"
        />
      </div>

      {/* --- Cover --- */}
      <div className="col-md-6">
        <ImageField
          label="Cover image"
          value={f.cover_url}
          onChange={(url) => setF((prev) => ({ ...prev, cover_url: url }))}
          aspect="wide"
          previewSize={{ width: 96, height: 64 }}
          testIdPrefix="bf-cover"
        />
      </div>

      {/* --- Submit --- */}
      <div className="col-12 d-flex justify-content-end gap-2 mt-2">
        <button className="btn-ap" type="submit" disabled={saving} data-testid="bf-submit">
          {saving ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

/* ============================================================
   GALLERY — with Upload + URL options
   ============================================================ */
function Gallery({ biz, reload }) {
  const [image_url, setImg] = useState('');
  const [caption, setCap] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const fileRef = useRef(null);

  const add = async (e) => {
    e.preventDefault();
    setUploadErr('');
    if (!image_url.trim()) {
      setUploadErr('Please paste an image URL or upload a file first.');
      return;
    }
    try {
      await api.post(`/owner/businesses/${biz.id}/gallery`, { image_url, caption });
      setImg('');
      setCap('');
      if (fileRef.current) fileRef.current.value = '';
      reload();
    } catch (e) {
      setUploadErr(apiError(e) || 'Failed to add image.');
    }
  };

  const del = async (g) => {
    if (window.confirm('Remove image?')) {
      await api.delete(`/owner/businesses/${biz.id}/gallery/${g.id}`);
      reload();
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploadErr('');
    if (file.size > 5 * 1024 * 1024) {
      setUploadErr('Image is too large. Max size is 5 MB.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url =
        data?.url || data?.file_url || data?.image_url ||
        data?.path || data?.data?.url || '';
      if (!url) throw new Error('Upload succeeded but no URL was returned.');
      setImg(url);
    } catch (e) {
      setUploadErr(
        'Upload failed: ' +
        (apiError(e) || e.message || 'Unknown error') +
        '. You can still paste an image URL manually.'
      );
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = '';
  };

  return (
    <div className="ap-card" data-testid="owner-gallery">
      {uploadErr && (
        <div className="alert alert-danger py-2 small" data-testid="gal-error">
          {uploadErr}
        </div>
      )}

      <form onSubmit={add} className="row g-2 mb-3 align-items-start">
        <div className="col-md-7">
          <label className="form-label small fw-semibold mb-1">Image</label>
          <div className="d-flex gap-2">
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                className="form-control"
                placeholder="Paste an image URL, or click Upload →"
                value={image_url}
                onChange={(e) => setImg(e.target.value)}
                data-testid="gal-url"
                style={{ paddingRight: image_url ? 38 : 12 }}
              />
              {image_url && (
                <button
                  type="button"
                  onClick={() => {
                    setImg('');
                    if (fileRef.current) fileRef.current.value = '';
                  }}
                  style={{
                    position: 'absolute', right: 6, top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none', background: 'transparent',
                    cursor: 'pointer', color: '#94a3b8',
                    fontSize: '1.1rem', lineHeight: 1,
                  }}
                  aria-label="Clear image URL"
                >
                  ×
                </button>
              )}
            </div>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="gal-upload-btn"
              style={{ whiteSpace: 'nowrap' }}
            >
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onPickFile}
              data-testid="gal-file"
            />
          </div>
          <div className="form-text small text-muted-2">
            JPG, PNG, GIF or WEBP · max 5 MB
          </div>
        </div>

        <div className="col-md-3">
          <label className="form-label small fw-semibold mb-1">Caption</label>
          <input
            className="form-control"
            placeholder="Optional"
            value={caption}
            onChange={(e) => setCap(e.target.value)}
            data-testid="gal-cap"
          />
        </div>

        <div className="col-md-2 d-flex align-items-end">
          <button
            className="btn-ap w-100"
            disabled={uploading || !image_url.trim()}
            data-testid="gal-add"
          >
            Add
          </button>
        </div>

        {image_url && (
          <div className="col-12">
            <div
              className="d-flex align-items-center gap-2 mt-2 p-2"
              style={{
                background: '#F8FAFF',
                border: '1px solid #E0E7FF',
                borderRadius: 10,
              }}
            >
              <img
                src={image_url}
                alt="Preview"
                style={{
                  width: 80, height: 60, objectFit: 'cover',
                  borderRadius: 8, border: '1px solid #e2e8f0',
                }}
                onError={(e) => {
                  e.currentTarget.style.opacity = '0.3';
                  e.currentTarget.title = 'Preview unavailable';
                }}
              />
              <div className="small">
                <div className="fw-semibold">Preview</div>
                <div
                  className="text-muted-2"
                  style={{
                    maxWidth: 420,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {image_url}
                </div>
                {uploading && <div style={{ color: '#4338CA' }}>Uploading…</div>}
              </div>
            </div>
          </div>
        )}
      </form>

      <div className="row g-3">
        {(biz.gallery || []).map((g) => (
          <div className="col-6 col-md-3" key={g.id}>
            <div style={{ position: 'relative' }}>
              <img
                src={g.image_url}
                alt={g.caption || ''}
                style={{
                  width: '100%', height: 120, objectFit: 'cover',
                  borderRadius: 10,
                }}
              />
              {g.caption && (
                <div
                  style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    padding: '0.35rem 0.5rem', fontSize: '0.72rem',
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
                    color: '#fff',
                    borderBottomLeftRadius: 10,
                    borderBottomRightRadius: 10,
                    whiteSpace: 'nowrap', overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {g.caption}
                </div>
              )}
              <button
                className="btn btn-sm btn-danger"
                style={{ position: 'absolute', top: 6, right: 6 }}
                onClick={() => del(g)}
                data-testid={`gal-del-${g.id}`}
              >
                ×
              </button>
            </div>
          </div>
        ))}
        {(biz.gallery || []).length === 0 && (
          <div className="col-12 text-center text-muted py-4">
            No gallery images yet. Upload one or paste an image URL above.
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   PRODUCTS (unchanged)
   ============================================================ */
function Products({ biz, reload }) {
  const [f, setF] = useState({ name:'', description:'', price:'', image_url:'' });
  const add = async (e) => { e.preventDefault(); await api.post(`/owner/businesses/${biz.id}/products`, f); setF({ name:'',description:'',price:'',image_url:'' }); reload(); };
  const del = async (p) => { if (window.confirm('Remove product?')) { await api.delete(`/owner/businesses/${biz.id}/products/${p.id}`); reload(); } };
  return (
    <div className="ap-card" data-testid="owner-products">
      <form onSubmit={add} className="row g-2 mb-3">
        <div className="col-md-3"><input className="form-control" placeholder="Name" required value={f.name} onChange={e=>setF({...f,name:e.target.value})} data-testid="prod-name"/></div>
        <div className="col-md-3"><input className="form-control" placeholder="Price" value={f.price} onChange={e=>setF({...f,price:e.target.value})} data-testid="prod-price"/></div>
        <div className="col-md-4"><input className="form-control" placeholder="Image URL" value={f.image_url} onChange={e=>setF({...f,image_url:e.target.value})} data-testid="prod-img"/></div>
        <div className="col-md-2"><button className="btn-ap w-100" data-testid="prod-add">Add</button></div>
        <div className="col-12"><input className="form-control" placeholder="Description" value={f.description} onChange={e=>setF({...f,description:e.target.value})} data-testid="prod-desc"/></div>
      </form>
      <table className="ap-table">
        <thead><tr><th>Name</th><th>Price</th><th></th></tr></thead>
        <tbody>
          {(biz.products||[]).map(p => (
            <tr key={p.id}>
              <td>{p.name}<div className="small text-muted-2">{p.description}</div></td>
              <td>{p.price ? '₹ '+p.price : '—'}</td>
              <td><button className="btn btn-sm text-danger" onClick={()=>del(p)} data-testid={`prod-del-${p.id}`}>Delete</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ============================================================
   SERVICES (unchanged)
   ============================================================ */
function Services({ biz, reload }) {
  const [f, setF] = useState({ name:'', description:'', price:'' });
  const add = async (e) => { e.preventDefault(); await api.post(`/owner/businesses/${biz.id}/services`, f); setF({ name:'',description:'',price:'' }); reload(); };
  const del = async (s) => { if (window.confirm('Remove service?')) { await api.delete(`/owner/businesses/${biz.id}/services/${s.id}`); reload(); } };
  return (
    <div className="ap-card" data-testid="owner-services">
      <form onSubmit={add} className="row g-2 mb-3">
        <div className="col-md-4"><input className="form-control" placeholder="Service name" required value={f.name} onChange={e=>setF({...f,name:e.target.value})} data-testid="svc-name"/></div>
        <div className="col-md-3"><input className="form-control" placeholder="Price" value={f.price} onChange={e=>setF({...f,price:e.target.value})} data-testid="svc-price"/></div>
        <div className="col-md-3"><input className="form-control" placeholder="Description" value={f.description} onChange={e=>setF({...f,description:e.target.value})} data-testid="svc-desc"/></div>
        <div className="col-md-2"><button className="btn-ap w-100" data-testid="svc-add">Add</button></div>
      </form>
      <ul className="list-unstyled">
        {(biz.services||[]).map(s => (
          <li key={s.id} className="d-flex justify-content-between border-bottom py-2">
            <div>
              <div className="fw-semibold">{s.name}</div>
              <div className="small text-muted-2">{s.description}</div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span>{s.price ? '₹ '+s.price : '—'}</span>
              <button className="btn btn-sm text-danger" onClick={()=>del(s)} data-testid={`svc-del-${s.id}`}>Delete</button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ============================================================
   HOURS (unchanged)
   ============================================================ */
function Hours({ biz, reload }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const days = Array.from({length:7}, (_,d) => {
      const existing = (biz.hours||[]).find(x => x.day_of_week === d);
      return existing ? { day_of_week:d, is_closed: !!existing.is_closed, open_time: existing.open_time?.slice(0,5) || '09:00', close_time: existing.close_time?.slice(0,5) || '19:00' } :
        { day_of_week:d, is_closed:d===0, open_time:'09:00', close_time:'19:00' };
    });
    setRows(days);
  }, [biz]);

  const save = async () => {
    const hours = rows.map(r => ({ ...r, open_time: r.open_time + ':00', close_time: r.close_time + ':00' }));
    await api.put(`/owner/businesses/${biz.id}/hours`, { hours });
    reload();
    alert('Hours saved');
  };

  return (
    <div className="ap-card" data-testid="owner-hours">
      {rows.map((r,i) => (
        <div key={i} className="d-flex align-items-center gap-2 py-2 border-bottom">
          <div style={{width:80}}><strong>{DAYS[r.day_of_week]}</strong></div>
          <label className="d-flex align-items-center gap-1"><input type="checkbox" checked={r.is_closed} onChange={e=>{ const n=[...rows]; n[i]={...r, is_closed:e.target.checked}; setRows(n); }} data-testid={`hour-closed-${i}`}/> Closed</label>
          <input type="time" className="form-control form-control-sm" style={{maxWidth:120}} value={r.open_time} disabled={r.is_closed} onChange={e=>{ const n=[...rows]; n[i]={...r, open_time:e.target.value}; setRows(n); }} data-testid={`hour-open-${i}`}/>
          <span>–</span>
          <input type="time" className="form-control form-control-sm" style={{maxWidth:120}} value={r.close_time} disabled={r.is_closed} onChange={e=>{ const n=[...rows]; n[i]={...r, close_time:e.target.value}; setRows(n); }} data-testid={`hour-close-${i}`}/>
        </div>
      ))}
      <button className="btn-ap mt-3" onClick={save} data-testid="hours-save">Save hours</button>
    </div>
  );
}